import { useState } from 'react';
import { hostOf, externalHref } from '../lib/url';

export interface Win {
  query: string;
  state: 'mentioned' | 'cited_only';
  citations?: string[];
  detail?: string;
}

/** Assistants that accept a prefilled question in the URL. Gemini has no such
 * parameter, so it gets the copy button instead of a broken link. */
const ASK_LINKS: { label: string; href: (q: string) => string }[] = [
  { label: 'ChatGPT', href: (q) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { label: 'Perplexity', href: (q) => `https://www.perplexity.ai/search?q=${encodeURIComponent(q)}` },
  { label: 'Claude', href: (q) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { label: 'Google AI Mode', href: (q) => `https://www.google.com/search?udm=50&q=${encodeURIComponent(q)}` },
];

function Copy({ text, label }: { text: string; label: string }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => void navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })}
      className="font-mono text-xs border border-cyan/40 text-cyan rounded px-2 py-1 hover:bg-cyan/10 transition"
    >
      {done ? 'copied ✓' : label}
    </button>
  );
}

/**
 * What to do with the questions you already win.
 *
 * A win is worth more than a tick: it tells you which pages earned a citation,
 * which is the only reliable evidence of what to make more of. So this panel
 * does three things — proves it (anyone can re-ask the question themselves),
 * explains it (the sources behind the answers, aggregated), and says plainly
 * what does NOT work, because the obvious idea here is a wrong one.
 */
export default function WinsPanel({ wins, brand, domain }: { wins: Win[]; brand: string; domain?: string }): JSX.Element | null {
  if (wins.length === 0) return null;
  const own = (h: string): boolean => Boolean(domain) && (h === domain || h.endsWith(`.${domain}`));

  // Which domains keep turning up behind the answers that name you. This is the
  // actionable half: those pages are why you won, so go get more of them.
  const sourceCounts = new Map<string, number>();
  // Your own winning URLs, kept whole rather than reduced to a hostname: the
  // useful answer to "what worked" is a page, not a domain.
  const ownPages = new Map<string, number>();
  for (const w of wins) {
    for (const c of w.citations ?? []) {
      const h = hostOf(c);
      if (!h) continue;
      if (own(h)) { ownPages.set(c, (ownPages.get(c) ?? 0) + 1); continue; }
      sourceCounts.set(h, (sourceCounts.get(h) ?? 0) + 1);
    }
  }
  const sources = [...sourceCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  const myPages = [...ownPages.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  const mentioned = wins.filter((w) => w.state === 'mentioned').length;

  return (
    <div className="border border-success/40 bg-success/5 rounded-lg p-4 mb-6">
      <h3 className="font-serif text-xl mb-1">You already win {wins.length} {wins.length === 1 ? 'question' : 'questions'}</h3>
      <p className="text-sm text-text-secondary">
        {mentioned > 0 && <>{mentioned} where AI names {brand} in the answer itself</>}
        {mentioned > 0 && wins.length > mentioned && ', '}
        {wins.length > mentioned && <>{wins.length - mentioned} where your page is cited as a source but your name isn&apos;t said out loud</>}
        . These are the most useful lines in the whole report, not because they&apos;re good news, but because they&apos;re
        the only place you can see <span className="text-text-primary">what actually worked</span>.
      </p>

      {/* 1 — the evidence */}
      <div className="mt-4 space-y-2">
        {wins.map((w, i) => (
          <div key={i} className="border border-warm-dark-2 rounded-lg p-3 bg-warm-dark/40">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <p className="text-sm text-text-primary">{w.query}</p>
              <span className={`font-mono text-[11px] uppercase tracking-wide font-semibold rounded px-2 py-0.5 shrink-0 ${w.state === 'mentioned' ? 'bg-success text-warm-dark' : 'bg-[#E0B341] text-warm-dark'}`}>
                {w.state === 'mentioned' ? 'NAMED' : 'CITED'}
              </span>
            </div>
            {w.detail && <p className="font-mono text-xs text-text-secondary mt-1">{w.detail}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="font-mono text-xs text-text-secondary">ask it yourself:</span>
              {ASK_LINKS.map((a) => (
                <a key={a.label} href={a.href(w.query)} target="_blank" rel="noreferrer"
                  className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-1 text-text-secondary hover:text-cyan hover:border-cyan/40 transition">
                  {a.label} ↗
                </a>
              ))}
              <Copy text={w.query} label="copy (for Gemini)" />
            </div>
          </div>
        ))}
      </div>

      {/* 2a — YOUR pages that earned it: the most directly actionable line here */}
      {myPages.length > 0 && (
        <div className="mt-4 border-t border-warm-dark-2 pt-3">
          <p className="text-sm text-text-primary">Your pages that earned these answers</p>
          <p className="text-sm text-text-secondary mb-2">
            AI cited these of your own pages. This is the template, whatever these do, the pages that lose don&apos;t.
            Open one, and copy its shape for your other topics.
          </p>
          <ul className="space-y-1">
            {myPages.map(([url, n]) => (
              <li key={url} className="text-sm truncate">
                <a href={externalHref(url)} target="_blank" rel="noreferrer" className="text-cyan hover:underline">{url.replace(/^https?:\/\//, '')}</a>
                {n > 1 && <span className="font-mono text-xs text-cyan/70"> ×{n}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 2b — the third-party pages behind the answers */}
      {sources.length > 0 && (
        <div className="mt-4 border-t border-warm-dark-2 pt-3">
          <p className="text-sm text-text-primary">Other sites cited alongside you</p>
          <p className="text-sm text-text-secondary mb-2">
            The third-party sources the models read to produce those answers. These domains already carry weight for
            your topic, so being named on them is not guesswork. It is a list.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {sources.map(([host, n]) => (
              <span key={host} className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-1 text-text-secondary">
                {host}{n > 1 && <span className="text-cyan/70"> ×{n}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 3 — what to do, and the thing not to bother with */}
      <div className="mt-4 border-t border-warm-dark-2 pt-3 space-y-2 text-sm text-text-secondary">
        <p>
          <span className="text-text-primary">Make more of what worked.</span> Look at the winning question and the page
          it maps to. Publish the same shape for your other topics, one page per question, the answer in the first two
          sentences, the specific names in the heading.
        </p>
        <p>
          <span className="text-text-primary">Use it as proof.</span> &ldquo;Ask ChatGPT this question and it recommends
          us&rdquo; is a claim a prospect can check in ten seconds, which is what makes it land. Put the question on your
          site, in a deck, in a proposal.
        </p>
        <p>
          <span className="text-text-primary">Watch that it holds.</span> These answers are not stable, models change,
          competitors publish. Re-run the same approved questions on a schedule and you&apos;ll see a win slip before a
          customer does.
        </p>
        <p className="border border-estimated/40 bg-estimated/5 rounded-lg p-3">
          <span className="text-text-primary">Worth knowing: clicking those links does not improve your position.</span>{' '}
          It is tempting to put a &ldquo;see what AI says about us&rdquo; button on your site and send traffic through it.
          Assistants don&apos;t rank by click-through the way search engines do, and there is no known path from someone
          opening a chat back into what a model retrieves or recommends. So do it because it is good proof for a visitor
, not because it will move your ranking. What actually moves it is more independent pages naming you, which is
          step 4.
        </p>
      </div>
    </div>
  );
}
